import { useAuth } from 'react-oidc-context'
import { Navigate, useLocation } from 'react-router-dom'
import type { Permission } from './permissions'
import { hasPermission } from './permissions'

interface Props {
  children: React.ReactNode
  /** If set, the user must have this permission to access the route. */
  require?: Permission
}

/**
 * ProtectedRoute guards a route behind OIDC authentication.
 * - Unauthenticated users are redirected to /login.
 * - Authenticated users lacking the required permission see a 403 page.
 */
export function ProtectedRoute({ children, require }: Props) {
  const auth = useAuth()
  const location = useLocation()

  if (auth.isLoading) {
    return <div className="loading-screen">Authenticating…</div>
  }

  if (!auth.isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (require && !hasPermission(auth.user, require)) {
    return (
      <div className="error-screen">
        <h2>Access Denied</h2>
        <p>You do not have permission to view this page.</p>
      </div>
    )
  }

  return <>{children}</>
}
